import { QueryClient } from '@tanstack/react-query';

import { ApiError } from './api/client';

/**
 * Shared React Query client. Auth errors are never retried (a refresh already
 * happened in the API layer); transient errors get a couple of retries.
 *
 * `staleTime: 0` — every route's data is considered stale the instant it's
 * fetched, so navigating to a page (e.g. via the sidebar) always triggers a
 * background revalidation instead of silently reusing up-to-30s-old cache.
 * The cached value still paints immediately (no loading flicker); this only
 * changes whether a refetch fires behind it.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && (error.isAuthError || error.status < 500)) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/** Centralised query keys so invalidation stays consistent and typo-proof. */
export const queryKeys = {
  me: ['client-auth', 'me'] as const,
  packages: { list: ['packages', 'list'] as const },
  clientQuestionnaires: {
    list: ['client-questionnaires', 'list'] as const,
    detail: (id: string) => ['client-questionnaires', 'detail', id] as const,
  },
  clientReports: {
    list: ['client-reports', 'list'] as const,
    detail: (id: string) => ['client-reports', 'detail', id] as const,
  },
  clientBookings: {
    slots: (from: string, to: string) => ['client-bookings', 'slots', from, to] as const,
    list: ['client-bookings', 'list'] as const,
  },
  clientPurchases: {
    list: ['client-purchases', 'list'] as const,
    active: ['client-purchases', 'active'] as const,
    detail: (id: string) => ['client-purchases', 'detail', id] as const,
  },
  bankAccounts: { list: ['bank-accounts', 'list'] as const },
  fingerprintImages: { list: ['fingerprint-images', 'list'] as const },
  resources: { list: ['resources', 'list'] as const },
};
